import * as PIXI from 'pixi.js';
import { objectLayersConfig } from '../config/ObjectLayerConfig';
import type { Camera } from '../systems/Camera';

export interface ObjectLayer {
  sprite: PIXI.Sprite;
  zIndex: number;
  position: { x: number; y: number };
  id?: string; // Optional identifier for the layer
}

export class World {
  public container: PIXI.Container;
  public bounds: PIXI.Rectangle;
  private backgroundSprite: PIXI.Sprite | null = null;
  private backgroundLoadPromise: Promise<void>;
  private objectLayers: ObjectLayer[] = [];
  private objectLayersContainer: PIXI.Container;
  private actualWorldWidth: number = 0;
  private actualWorldHeight: number = 0;

  constructor(width: number, height: number) {
    this.container = new PIXI.Container();
    this.bounds = new PIXI.Rectangle(0, 0, width, height);
    
    // Create a separate container for object layers
    this.objectLayersContainer = new PIXI.Container();
    this.container.addChild(this.objectLayersContainer);
    
    this.backgroundLoadPromise = this.generateWorld();
  }

  public async waitForBackgroundLoad(): Promise<void> {
    return this.backgroundLoadPromise;
  }

  public getActualWorldDimensions(): { width: number; height: number } {
    return { width: this.actualWorldWidth, height: this.actualWorldHeight };
  }

  private async generateWorld(): Promise<void> {
    // Load and create the background image
    await this.createImageBackground();
    
    // Object layers will be added on top of background
    this.container.addChild(this.objectLayersContainer);
    
    // Object layers are now initialized via initializeObjectLayersWithCamera
    // to ensure proper z-index with player and other game elements
    // await this.initializeObjectLayersFromConfig();
  }

  private async createImageBackground(): Promise<void> {
    try {
      // Load the background image
      const texture = await PIXI.Assets.load('/assets/background/map_v11.jpg');
      this.backgroundSprite = new PIXI.Sprite(texture);

      const scale = 1.2;
      this.actualWorldWidth = texture.width * scale;
      this.actualWorldHeight = texture.height * scale;

      // Update world bounds to match the scaled image
      this.bounds.width = this.actualWorldWidth;
      this.bounds.height = this.actualWorldHeight;

      this.backgroundSprite.scale.set(scale);

      // Position the background at origin (0,0)
      this.backgroundSprite.x = 0;
      this.backgroundSprite.y = 0;

      // Add the background to the container
      this.container.addChild(this.backgroundSprite);
    } catch (error) {
      console.warn('Failed to load background image, falling back to generated background:', error);
      this.createFallbackBackground();
    }
  }

  private createFallbackBackground(): void {
    // Fallback to tiled background if image fails to load
    // Use original world bounds as fallback
    this.actualWorldWidth = this.bounds.width;
    this.actualWorldHeight = this.bounds.height;

    const tileSize = 64;
    for (let x = 0; x < this.bounds.width; x += tileSize) {
      for (let y = 0; y < this.bounds.height; y += tileSize) {
        const tile = new PIXI.Graphics();

        // Alternate colors for a checkerboard pattern
        const isEven = (x / tileSize + y / tileSize) % 2 === 0;
        const color = isEven ? 0x2c3e50 : 0x34495e;

        tile.beginFill(color);
        tile.drawRect(0, 0, tileSize, tileSize);
        tile.endFill();

        // Add subtle border
        tile.lineStyle(1, 0x1a252f, 0.3);
        tile.drawRect(0, 0, tileSize, tileSize);

        tile.x = x;
        tile.y = y;

        this.container.addChild(tile);
      }
    }
  }

  /**
   * Add a new object layer to the world
   * @param assetPath Path to the image asset
   * @param x Position x coordinate
   * @param y Position y coordinate
   * @param zIndex Layer order (higher numbers appear on top)
   * @param scale Optional scale factor for the sprite
   * @returns Promise that resolves with the created ObjectLayer
   */
  public async addObjectLayer(
    assetPath: string,
    x: number,
    y: number,
    zIndex: number = 1,
    scale: number = 1
  ): Promise<ObjectLayer> {
    try {
      // Load the object texture
      const texture = await PIXI.Assets.load(assetPath);
      const sprite = new PIXI.Sprite(texture);
      
      // Set position and scale
      sprite.x = x;
      sprite.y = y;
      sprite.scale.set(scale);
      
      // Create the object layer
      const objectLayer: ObjectLayer = {
        sprite,
        zIndex,
        position: { x, y }
      };
      
      // Add to our layers array
      this.objectLayers.push(objectLayer);
      
      // Add to container with proper z-index
      sprite.zIndex = zIndex;
      this.objectLayersContainer.addChild(sprite);
      
      // Make sure the container sorts children by zIndex
      this.objectLayersContainer.sortableChildren = true;
      this.objectLayersContainer.sortChildren();
      
      return objectLayer;
    } catch (error) {
      console.error('Failed to load object layer:', error);
      throw error;
    }
  }
  
  /**
   * Get all object layers
   */
  public getObjectLayers(): ObjectLayer[] {
    return this.objectLayers;
  }
  
  /**
   * Remove an object layer
   */
  public removeObjectLayer(objectLayer: ObjectLayer): void {
    const index = this.objectLayers.indexOf(objectLayer);
    if (index !== -1) {
      this.objectLayersContainer.removeChild(objectLayer.sprite);
      this.objectLayers.splice(index, 1);
    }
  }
  
  /**
   * Find an object layer by ID
   * @param id The unique identifier of the layer
   */
  public getObjectLayerById(id: string): ObjectLayer | undefined {
    return this.objectLayers.find(layer => layer.id === id);
  }
  
  /**
   * Initialize object layers with direct access to camera for proper z-indexing
   * @param camera The camera instance to add objects to
   */
  public async initializeObjectLayersWithCamera(camera: Camera): Promise<void> {
    // Load all auto-load layers from config and add directly to camera
    const loadPromises = objectLayersConfig
      .filter(config => config.autoLoad !== false)
      .map(async (config) => {
        try {
          // Load the object texture
          const texture = await PIXI.Assets.load(config.assetPath);
          const sprite = new PIXI.Sprite(texture);
          
          // Set position and scale
          sprite.x = config.x;
          sprite.y = config.y;
          sprite.scale.set(config.scale || 1);
          
          // Create the object layer
          const objectLayer: ObjectLayer = {
            sprite,
            zIndex: config.zIndex,
            position: { x: config.x, y: config.y },
            id: config.id
          };
          
          // Add to our layers array for tracking
          this.objectLayers.push(objectLayer);
          
          // Add directly to camera with proper z-index
          camera.addToWorld(sprite, config.zIndex);
          
          return objectLayer;
        } catch (error) {
          console.error(`Failed to load object layer ${config.id}:`, error);
          throw error;
        }
      });
    
    await Promise.all(loadPromises);
    console.log(`Loaded ${loadPromises.length} object layers directly to camera`);
  }
  
  public update(_deltaTime: number): void {
    // World can have its own animations or updates here
    // For example, swaying trees, moving clouds, etc.
  }
}
